/**
 * Conversation operations — load, select, create, delete, ensure.
 */

import { runInAction } from 'mobx';
import { saveActiveConversation } from '../persistence';
import { getDeviceId } from '../persistence/device-id';
import {
  fetchConversations, createConversation,
  fetchConversationMessages, deleteConversationApi,
} from '@api/conversations';
import type { ChatState } from './chat-state';

/** Load conversation list from server */
export async function loadConversations(state: ChatState): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) return;
  try {
    const list = await fetchConversations(deviceId);
    runInAction(() => {
      state.conversations = list;
      state.isConversationsLoaded = true;
    });
  } catch {
    // Server unavailable — stay in local-only mode
  }
}

/** Switch to an existing conversation */
export async function selectConversation(state: ChatState, id: string): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) return;
  try {
    const serverMessages = await fetchConversationMessages(id, deviceId);
    runInAction(() => {
      state.activeConversationId = id;
      state.messages = serverMessages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
        stats: m.statsJson ? JSON.parse(m.statsJson) : undefined,
        intent: m.intent ?? undefined,
      }));
      saveActiveConversation(id);
      state.persistLocal();
      state.isConversationListVisible = false;
    });
  } catch {
    // Failed to load — keep current messages
  }
}

/** Start a new conversation */
export async function startNewConversation(state: ChatState): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) {
    state.clearHistory();
    return;
  }
  try {
    const result = await createConversation(deviceId);
    runInAction(() => {
      state.activeConversationId = result.id;
      state.messages = [];
      saveActiveConversation(result.id);
      state.persistLocal();
      if (result.prunedId) {
        state.conversations = state.conversations.filter((c) => c.id !== result.prunedId);
      }
      state.conversations.unshift({
        id: result.id,
        title: 'New conversation',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageCount: 0,
      });
      state.isConversationListVisible = false;
    });
  } catch {
    state.clearHistory();
  }
}

/** Delete a conversation */
export async function deleteConversation(state: ChatState, id: string): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) return;
  try {
    await deleteConversationApi(id, deviceId);
  } catch {
    // Best-effort delete
  }
  runInAction(() => {
    state.conversations = state.conversations.filter((c) => c.id !== id);
    if (state.activeConversationId === id) {
      state.activeConversationId = null;
      state.messages = [];
      saveActiveConversation(null);
      state.persistLocal();
    }
  });
}

/** Auto-create a server conversation if none is active */
export async function ensureConversation(state: ChatState): Promise<void> {
  if (state.activeConversationId) return;
  const deviceId = getDeviceId();
  if (!deviceId) return;
  try {
    const result = await createConversation(deviceId);
    runInAction(() => {
      state.activeConversationId = result.id;
      saveActiveConversation(result.id);
      if (result.prunedId) {
        state.conversations = state.conversations.filter((c) => c.id !== result.prunedId);
      }
      state.conversations.unshift({
        id: result.id,
        title: 'New conversation',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messageCount: 0,
      });
    });
  } catch {
    // Continue without server conversation
  }
}
